import { motion, useReducedMotion } from "motion/react"
import { cx } from "@/lib/utils/cx"
import type { DamageZone } from "../types"
import { CarDrawing } from "./car-drawing"
import { CORNER_STAND, closerStand, panelStand, type StandPoint } from "./car-plan"
import { angleDiff, atCorner, STAND_BEARING } from "./motion/turn"
import type { Corner, PlannedShot } from "./types"

// "Stand here, look there." A small top-down car with a person dot at the
// spot to shoot from and a view cone toward what to photograph. Between shots
// the dot walks from the last spot to the next one (the walk card).

// Car drawing bounds (mirrors and wheels included) in car-plan units.
const CAR_BOUNDS = { minX: 14, maxX: 226, minY: 10, maxY: 430 }
const MARGIN = 34

// Frame the car plus wherever the person stands — no dead space.
function viewBoxFor(points: Array<StandPoint | null | undefined>): string {
  let { minX, maxX, minY, maxY } = CAR_BOUNDS
  for (const p of points) {
    if (!p) continue
    minX = Math.min(minX, p.x - MARGIN)
    maxX = Math.max(maxX, p.x + MARGIN)
    minY = Math.min(minY, p.y - MARGIN)
    maxY = Math.max(maxY, p.y + MARGIN)
  }
  return `${minX} ${minY} ${maxX - minX} ${maxY - minY}`
}

export function standFor(shot: PlannedShot, plan: readonly PlannedShot[]): StandPoint | null {
  switch (shot.kind) {
    case "wide":
      return shot.corner ? CORNER_STAND[shot.corner] : null
    case "closer": {
      const zones = plan
        .filter((s) => s.kind === "closeup" && s.corner === shot.corner && s.zone)
        .map((s) => s.zone as DamageZone)
      return shot.corner ? closerStand(zones, shot.corner) : null
    }
    case "closeup":
      return shot.zone ? panelStand(shot.zone, 30) : null
    case "sticker":
      return panelStand("front-door-left", 34)
  }
}

// Where a bearing round the car (motion/turn.ts) sits on this map: between
// the two corner stand points either side of it, along an oval through all
// four.
const MAP_CENTER = { x: CORNER_STAND.FL.lookX, y: CORNER_STAND.FL.lookY }
const MAP_ANGLE: Record<Corner, number> = { FL: -45, FR: 45, RR: 135, RL: -135 }
const OVAL = {
  rx: (CORNER_STAND.FR.x - MAP_CENTER.x) / Math.sin(Math.PI / 4),
  ry: (MAP_CENTER.y - CORNER_STAND.FR.y) / Math.cos(Math.PI / 4),
}

export function mapPointFor(bearing: number): { x: number; y: number } {
  const order: Corner[] = ["FL", "FR", "RR", "RL"]
  let t = 0
  for (let i = 0; i < 4; i++) {
    const a = order[i]
    const b = order[(i + 1) % 4]
    const span = angleDiff(STAND_BEARING[b], STAND_BEARING[a])
    const into = angleDiff(bearing, STAND_BEARING[a])
    if (into >= 0 && into <= span) {
      t = MAP_ANGLE[a] + (into / span) * angleDiff(MAP_ANGLE[b], MAP_ANGLE[a])
      break
    }
  }
  const r = (t * Math.PI) / 180
  return { x: MAP_CENTER.x + OVAL.rx * Math.sin(r), y: MAP_CENTER.y - OVAL.ry * Math.cos(r) }
}

function cone(p: StandPoint): string {
  const dx = p.lookX - p.x
  const dy = p.lookY - p.y
  const len = Math.hypot(dx, dy) || 1
  const ux = dx / len
  const uy = dy / len
  const reach = Math.min(len, 92)
  const spread = 0.42
  const ax = p.x + reach * (ux * Math.cos(spread) - uy * Math.sin(spread))
  const ay = p.y + reach * (uy * Math.cos(spread) + ux * Math.sin(spread))
  const bx = p.x + reach * (ux * Math.cos(-spread) - uy * Math.sin(-spread))
  const by = p.y + reach * (uy * Math.cos(-spread) + ux * Math.sin(-spread))
  return `M ${p.x} ${p.y} L ${ax} ${ay} L ${bx} ${by} Z`
}

export function StandMap({
  shot,
  plan,
  damage,
  from,
  walk = false,
  tone = "paper",
  className,
  label,
  here = null,
}: {
  shot: PlannedShot
  plan: readonly PlannedShot[]
  // Every damaged panel (lightly marked) — the current one is stronger.
  damage: readonly DamageZone[]
  // Where the person was for the previous shot (draws the walk).
  from?: StandPoint | null
  // Animate the dot along the walk (walk card); static otherwise.
  walk?: boolean
  tone?: "paper" | "dark"
  className?: string
  label?: string
  // Where the person is right now (a bearing round the car, from the motion
  // sensors). Drawn as a live dot that turns green at the stand point.
  here?: number | null
}) {
  const reduce = useReducedMotion()
  const to = standFor(shot, plan)
  const dark = tone === "dark"
  const focusZone = shot.zone
  const animated = walk && !reduce && !!from && !!to

  return (
    <svg
      // With the live dot, frame the whole walk so the dot never leaves the map.
      viewBox={viewBoxFor(here !== null ? [to, from, ...Object.values(CORNER_STAND)] : [to, from])}
      className={cx("h-auto w-full", className)}
      role="img"
      aria-label={label ?? "Where to stand"}
    >
      <CarDrawing
        marked={focusZone ? [focusZone] : damage}
        tone={tone}
        markedOpacity={focusZone ? 1 : 0.55}
      />
      {from && to && (
        <line
          x1={from.x}
          y1={from.y}
          x2={to.x}
          y2={to.y}
          stroke={dark ? "#ffffff" : "#0c2018"}
          strokeOpacity={0.35}
          strokeWidth={2}
          strokeDasharray="2 7"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {to && (
        <>
          <motion.path
            d={cone(to)}
            fill={dark ? "var(--color-signal-on-dark)" : "var(--color-signal)"}
            fillOpacity={0.22}
            initial={animated ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ delay: animated ? 0.75 : 0, duration: 0.25 }}
          />
          <motion.circle
            r={20}
            fill={here !== null ? "none" : dark ? "#ffffff" : "var(--color-ink)"}
            stroke={dark ? "#000000" : "#ffffff"}
            strokeWidth={5}
            initial={animated ? { cx: from!.x, cy: from!.y } : false}
            animate={{ cx: to.x, cy: to.y }}
            transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
            cx={to.x}
            cy={to.y}
          />
        </>
      )}
      {here !== null && <HereDot bearing={here} arrived={!!shot.corner && atCorner(here, shot.corner)} />}
    </svg>
  )
}

function HereDot({ bearing, arrived }: { bearing: number; arrived: boolean }) {
  const reduce = useReducedMotion()
  const p = mapPointFor(bearing)
  return (
    <motion.circle
      r={22}
      initial={false}
      animate={{ cx: p.x, cy: p.y, fill: arrived ? "var(--color-signal-on-dark)" : "#ffffff" }}
      transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 220, damping: 26 }}
      stroke="#000000"
      strokeWidth={5}
      data-testid="here-dot"
      data-arrived={arrived}
    />
  )
}
